import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Cat, 
  Heart, 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Users,
  Award,
  ArrowRight,
  Instagram,
  Facebook
} from 'lucide-react';

export default function LandingPage() {
  const { t } = useLanguage();
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <img 
              src="/logo.jpg" 
              alt="Gats del Carrer" 
              className="h-12 w-auto"
              data-testid="nav-logo"
            />
            
            <div className="hidden md:flex items-center gap-6">
              <button 
                onClick={() => scrollToSection('historia')}
                className="text-sm font-medium text-stone-600 hover:text-orange-600 transition-colors"
              >
                {t('nav_who')}
              </button>
              <button 
                onClick={() => scrollToSection('contacte')}
                className="text-sm font-medium text-stone-600 hover:text-orange-600 transition-colors"
              >
                {t('nav_contact')}
              </button>
              <LanguageSelector variant="compact" />
              <Button asChild className="rounded-full bg-orange-600 hover:bg-orange-700 px-6">
                <Link to="/login" data-testid="nav-client-area-btn">
                  <Cat size={16} className="mr-2" />
                  {t('nav_private_area')}
                </Link>
              </Button>
            </div>

            {/* Mobile menu */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector variant="compact" />
              <Button asChild variant="outline" size="sm" className="rounded-full">
                <Link to="/login">
                  <Cat size={14} />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-b from-stone-100 to-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-6">
                {t('hero_badge')}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 mb-6">
                {t('hero_title_1')}{' '}
                <span className="text-orange-600">{t('hero_title_2')}</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                {t('hero_description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => scrollToSection('contacte')}
                  className="rounded-full bg-orange-600 hover:bg-orange-700 px-8 py-6 text-base"
                  data-testid="hero-adopt-btn"
                >
                  <Heart size={18} className="mr-2" />
                  {t('hero_adopt')}
                </Button>
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-base"
                  onClick={() => scrollToSection('historia')}
                >
                  {t('hero_know_us')}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            </div>
            
            <div className="relative animate-slide-in">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1574158622682-e40e69881006?w=800&h=800&fit=crop"
                  alt="Gat"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">+500</p>
                    <p className="text-sm text-stone-500">{t('stat_adoptions')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Cat className="w-8 h-8 text-orange-600" />
              </div>
              <p className="text-3xl font-bold text-stone-900 mb-1">+500</p>
              <p className="text-sm text-stone-500">{t('stat_rescued')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-3xl font-bold text-stone-900 mb-1">+400</p>
              <p className="text-sm text-stone-500">{t('stat_adoptions')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-stone-900 mb-1">+50</p>
              <p className="text-sm text-stone-500">{t('stat_volunteers')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-stone-900 mb-1">+10</p>
              <p className="text-sm text-stone-500">{t('stat_years')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Historia Section */}
      <section id="historia" className="py-20 px-6 lg:px-12 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-4 block">
                {t('history_badge')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-6">
                {t('history_title')}
              </h2>
              
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p>{t('history_p1')}</p>
                <p>{t('history_p2')}</p>
                <p>{t('history_p3')}</p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  {t('history_tag1')}
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  {t('history_tag2')}
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  {t('history_tag3')}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1573865526739-10659fec78a5?w=400&h=500&fit=crop" alt="Gat 1" className="rounded-2xl shadow-lg w-full h-64 object-cover" />
              <img src="https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400&h=500&fit=crop" alt="Gat 2" className="rounded-2xl shadow-lg w-full h-64 object-cover mt-8" />
              <img src="https://images.unsplash.com/photo-1495360010541-f48722b34f7d?w=400&h=500&fit=crop" alt="Gat 3" className="rounded-2xl shadow-lg w-full h-64 object-cover" />
              <img src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?w=400&h=500&fit=crop" alt="Gat 4" className="rounded-2xl shadow-lg w-full h-64 object-cover mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('cta_title')}
          </h2>
          <p className="text-lg text-orange-100 mb-8">
            {t('cta_description')}
          </p>
          <Button 
            onClick={() => scrollToSection('contacte')}
            className="rounded-full bg-white text-orange-600 hover:bg-stone-100 px-8 py-6 text-base font-bold"
            data-testid="cta-contact-btn"
          >
            <Mail size={18} className="mr-2" />
            {t('cta_contact')}
          </Button>
        </div>
      </section>

      {/* Contacte Section */}
      <section id="contacte" className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-4 block">
              {t('contact_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
              {t('contact_title')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('contact_description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="bg-stone-50 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">{t('contact_phone')}</h3>
                  <p className="text-stone-600">651 626 346</p>
                </div>
              </div>

              <div className="bg-stone-50 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">{t('contact_email')}</h3>
                  <p className="text-stone-600">info@gatsdelcarrer.org</p>
                </div>
              </div>

              <div className="bg-stone-50 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">{t('contact_address')}</h3>
                  <p className="text-stone-600">C/ Corunya, 19</p>
                  <p className="text-stone-600">Sant Vicenç dels Horts, Barcelona</p>
                </div>
              </div>

              <div className="bg-stone-50 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">{t('contact_hours')}</h3>
                  <p className="text-stone-600">{t('contact_hours_weekdays')}</p>
                  <p className="text-stone-600">{t('contact_hours_saturday')}</p>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <a href="https://www.instagram.com/gatsdelcarrer/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center text-stone-600 hover:bg-orange-100 hover:text-orange-600 transition-colors" data-testid="instagram-link">
                  <Instagram size={20} />
                </a>
                <a href="https://www.facebook.com/Gatsdelcarrer" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center text-stone-600 hover:bg-orange-100 hover:text-orange-600 transition-colors" data-testid="facebook-link">
                  <Facebook size={20} />
                </a>
              </div>
            </div>

            <div className="bg-stone-50 rounded-3xl p-8">
              <h3 className="text-xl font-bold text-stone-900 mb-6">{t('contact_form_title')}</h3>
              
              <form className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{t('contact_form_name')}</label>
                  <input type="text" placeholder={t('contact_form_name_placeholder')} className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{t('contact_form_email')}</label>
                  <input type="email" placeholder="email@email.com" className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{t('contact_form_message')}</label>
                  <textarea rows={4} placeholder={t('contact_form_message_placeholder')} className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 resize-none" />
                </div>
                <Button type="submit" className="w-full rounded-full bg-orange-600 hover:bg-orange-700 py-6" data-testid="contact-submit-btn">
                  {t('contact_form_send')}
                </Button>
                <p className="text-xs text-stone-500 text-center">{t('contact_form_note')}</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="/logo.jpg" alt="Gats del Carrer" className="h-12 w-auto mb-4 rounded bg-white p-1" />
              <p className="text-stone-400 text-sm">{t('footer_description')}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footer_links')}</h4>
              <ul className="space-y-2 text-sm text-stone-400">
                <li><button onClick={() => scrollToSection('historia')} className="hover:text-white transition-colors">{t('nav_who')}</button></li>
                <li><button onClick={() => scrollToSection('contacte')} className="hover:text-white transition-colors">{t('nav_contact')}</button></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t('nav_private_area')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footer_contact')}</h4>
              <ul className="space-y-2 text-sm text-stone-400">
                <li>651 626 346</li>
                <li>info@gatsdelcarrer.org</li>
                <li>Sant Vicenç dels Horts, Barcelona</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-8 text-center text-sm text-stone-500">
            <p>© {new Date().getFullYear()} Gats del Carrer. {t('footer_rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
